// ClipList — reorderable timeline of staged clips.
// Supports native HTML5 drag-to-reorder plus up/down/remove controls.

import React, { useRef, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react';
import type { EditorClip } from './types';
import { formatBytes, formatDuration } from './format';

interface ClipListProps {
  clips: EditorClip[];
  disabled?: boolean;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onReorder: (from: number, to: number) => void;
}

export default function ClipList({ clips, disabled, onRemove, onMove, onReorder }: ClipListProps) {
  const dragFrom = useRef<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  return (
    <ol className="flex flex-col gap-2">
      {clips.map((clip, idx) => (
        <li
          key={clip.id}
          draggable={!disabled}
          onDragStart={() => {
            dragFrom.current = idx;
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIdx(idx);
          }}
          onDragLeave={() => setOverIdx((v) => (v === idx ? null : v))}
          onDrop={(e) => {
            e.preventDefault();
            if (dragFrom.current != null) onReorder(dragFrom.current, idx);
            dragFrom.current = null;
            setOverIdx(null);
          }}
          onDragEnd={() => {
            dragFrom.current = null;
            setOverIdx(null);
          }}
          className={[
            'flex items-center gap-3 rounded-lg p-2 pac-glass border transition-colors',
            overIdx === idx
              ? 'border-neon_purple-400/60'
              : 'border-neutral-700/40 hover:border-neutral-600/60',
            disabled ? 'opacity-60' : '',
          ].join(' ')}
        >
          {/* Drag handle + ordinal */}
          <div className="flex items-center gap-1 text-neutral-500 shrink-0">
            <GripVertical className="h-4 w-4 cursor-grab active:cursor-grabbing" />
            <span className="text-[11px] tabular-nums w-4 text-center">{idx + 1}</span>
          </div>

          {/* Thumbnail */}
          <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded bg-black/60">
            <video
              src={clip.url}
              muted
              playsInline
              preload="metadata"
              className="h-full w-full object-cover"
            />
          </div>

          {/* Meta */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-white/90" title={clip.name}>
              {clip.name}
            </p>
            <p className="text-[11px] text-neutral-500 tabular-nums">
              {formatDuration(clip.durationSec)} · {formatBytes(clip.size)}
              {clip.width && clip.height ? ` · ${clip.width}×${clip.height}` : ''}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              disabled={disabled || idx === 0}
              onClick={() => onMove(clip.id, -1)}
              aria-label="Move clip up"
              className="rounded p-1 text-neutral-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={disabled || idx === clips.length - 1}
              onClick={() => onMove(clip.id, 1)}
              aria-label="Move clip down"
              className="rounded p-1 text-neutral-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onRemove(clip.id)}
              aria-label="Remove clip"
              className="rounded p-1 text-neutral-400 hover:text-rose-300 hover:bg-rose-500/10 disabled:opacity-30"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </li>
      ))}
    </ol>
  );
}
